<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import { loadTenantLatencyTimeline, type InternetAvailabilityBucketDuration, type ProbeLatencyTimeline, type TenantLatencyTimeline } from "../api/measurements.js";
import type { TenantSummary } from "../api/tenants.js";
import AppShell from "../components/AppShell.vue";
import DateTimePicker from "../components/DateTimePicker.vue";
import LatencyChart from "../components/LatencyChart.vue";
import { formatDateTime } from "../formatting/date-time.js";
import { useTenantStore } from "../stores/tenants.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

type RangePreset = "live" | "today" | "this_week" | "this_month" | "last_month" | "this_year" | "last_year" | "custom";

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const tenant: ComputedRef<TenantSummary | null> = computed((): TenantSummary | null => tenantStore.selectedTenant);
const selectedRangePreset: Ref<RangePreset> = ref(userSettingsStore.settings.dashboardDefaultPreset);
const selectedRangeFrom: Ref<string> = ref(new Date(Date.now() - 24 * 3_600_000).toISOString());
const selectedRangeTo: Ref<string> = ref(new Date().toISOString());
const selectedBucketDuration: Ref<InternetAvailabilityBucketDuration> = ref(userSettingsStore.settings.dashboardDefaultResolution);
const timeline: Ref<TenantLatencyTimeline | null> = ref(null);
const latencyCursor: Ref<string | null> = ref(null);
const latencyCursorHistory: Ref<string[]> = ref([]);
const loading: Ref<boolean> = ref(false);
const loadingError: Ref<string | null> = ref(null);
const rangePickerDisabled: ComputedRef<boolean> = computed((): boolean => selectedRangePreset.value !== "custom");

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function resolutionMilliseconds(value: string): number | null {
  const match: RegExpExecArray | null = /^([1-9][0-9]*)(m|h|d)$/.exec(value);
  if (match === null) return null;
  const factor: number = match[2] === "m" ? 60_000 : match[2] === "h" ? 3_600_000 : 86_400_000;
  const milliseconds: number = Number(match[1]) * factor;
  return Number.isSafeInteger(milliseconds) ? milliseconds : null;
}

function updateRangeForPreset(preset: RangePreset): void {
  const now: Date = new Date();
  let from: Date = startOfLocalDay(now);
  let to: Date = now;
  if (preset === "live") {
    const durationMs: number | null = resolutionMilliseconds(selectedBucketDuration.value);
    if (durationMs === null) return;
    from = new Date(now.getTime() - 120 * durationMs);
  } else if (preset === "this_week") {
    from.setDate(from.getDate() - (now.getDay() + 6) % 7);
  } else if (preset === "this_month") {
    from = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (preset === "last_month") {
    from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    to = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (preset === "this_year") {
    from = new Date(now.getFullYear(), 0, 1);
  } else if (preset === "last_year") {
    from = new Date(now.getFullYear() - 1, 0, 1);
    to = new Date(now.getFullYear(), 0, 1);
  }
  selectedRangeFrom.value = from.toISOString();
  selectedRangeTo.value = to.toISOString();
}

async function loadTimeline(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !currentTenant.access.capabilities.includes("measurement.read")) {
    timeline.value = null;
    return;
  }
  loading.value = true;
  loadingError.value = null;
  try {
    if (selectedRangePreset.value !== "custom") updateRangeForPreset(selectedRangePreset.value);
    timeline.value = await loadTenantLatencyTimeline(currentTenant.id, selectedRangeFrom.value, selectedRangeTo.value, selectedBucketDuration.value, latencyCursor.value);
  } catch (error: unknown) {
    timeline.value = null;
    loadingError.value = error instanceof Error ? error.message : "Latency measurements are unavailable";
  } finally {
    loading.value = false;
  }
}

function selectRangePreset(): void {
  if (selectedRangePreset.value !== "custom") updateRangeForPreset(selectedRangePreset.value);
  void applySelection();
}

async function applySelection(): Promise<void> {
  latencyCursor.value = null;
  latencyCursorHistory.value = [];
  await loadTimeline();
}

async function showNextPage(): Promise<void> {
  const currentTimeline: TenantLatencyTimeline | null = timeline.value;
  if (currentTimeline?.nextCursor === null || currentTimeline === null) return;
  latencyCursorHistory.value = [...latencyCursorHistory.value, latencyCursor.value ?? currentTimeline.from];
  latencyCursor.value = currentTimeline.nextCursor;
  await loadTimeline();
}

async function showPreviousPage(): Promise<void> {
  const previousCursor: string | undefined = latencyCursorHistory.value.at(-1);
  if (previousCursor === undefined) return;
  latencyCursorHistory.value = latencyCursorHistory.value.slice(0, -1);
  latencyCursor.value = previousCursor === timeline.value?.from ? null : previousCursor;
  await loadTimeline();
}

function averagePrimaryLatency(probe: ProbeLatencyTimeline): string {
  const values: number[] = probe.points
    .map((point): number | null => point.primaryHttpLatencyMs)
    .filter((value): value is number => value !== null);
  if (values.length === 0) return "—";
  const total: number = values.reduce((sum: number, value: number): number => sum + value, 0);
  return `${Math.round(total / values.length)} ms`;
}

onMounted((): void => {
  void loadTimeline();
});
watch((): string | null => tenant.value?.id ?? null, (): void => { void loadTimeline(); });
</script>

<template>
  <AppShell v-if="tenant">
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ tenant.slug }}</p>
        <h1>Latency dashboard</h1>
        <p>Latency measured locally by each probe, grouped automatically for the selected period.</p>
      </div>
      <span class="status-pill"><span></span>{{ tenant.status }}</span>
    </header>

    <section class="content-panel latency-dashboard-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Monitoring</p>
          <h2>Latency by probe</h2>
          <p>HTTP, DNS, and gateway timings are shown in milliseconds. Choose a resolution appropriate to the selected period.</p>
        </div>
        <form class="availability-range-form" @submit.prevent="applySelection">
          <label>
            <span>Preset</span>
            <select v-model="selectedRangePreset" @change="selectRangePreset">
              <option value="live">Live</option><option value="today">Today</option><option value="this_week">This week</option><option value="this_month">This month</option><option value="last_month">Last month</option><option value="this_year">This year</option><option value="last_year">Last year</option><option value="custom">Custom</option>
            </select>
          </label>
          <label><span>From</span><DateTimePicker v-model="selectedRangeFrom" :date-time-format="userSettingsStore.settings.dateTimeFormat" ariaLabel="Latency range start" :disabled="rangePickerDisabled" /></label>
          <label><span>To</span><DateTimePicker v-model="selectedRangeTo" :date-time-format="userSettingsStore.settings.dateTimeFormat" ariaLabel="Latency range end" :disabled="rangePickerDisabled" /></label>
          <label><span>Resolution</span><input v-model="selectedBucketDuration" type="text" pattern="[1-9][0-9]*(m|h|d)" title="Positive minutes, hours, or days, for example 5m, 15m, 1h" required></label>
          <button class="button button--ghost" type="submit" :disabled="loading">Load</button>
        </form>
      </div>
      <p v-if="loading && timeline === null" class="placeholder-panel"><span class="loading-spinner" aria-hidden="true"></span>Loading latency measurements…</p>
      <p v-else-if="loadingError" class="form-error" role="alert">{{ loadingError }}</p>
      <p v-else-if="timeline === null" class="placeholder-panel">Latency measurements are unavailable.</p>
      <p v-else-if="timeline.probes.length === 0" class="placeholder-panel">No probes are registered for this tenant yet.</p>
      <div v-else class="latency-dashboard-list" :aria-busy="loading">
        <article v-for="probe in timeline.probes" :key="probe.probeId" class="latency-dashboard-card">
          <div class="latency-dashboard-card__heading"><div><h3>{{ probe.displayName }}</h3><p>{{ probe.points.length }} chart points</p></div><strong>{{ averagePrimaryLatency(probe) }}<small>average primary HTTP</small></strong></div>
          <LatencyChart :points="probe.points" :date-time-format="userSettingsStore.settings.dateTimeFormat" />
          <div class="pagination-controls latency-dashboard-card__pagination">
            <button class="button button--ghost" type="button" :disabled="latencyCursorHistory.length === 0 || loading" @click="showPreviousPage">Earlier</button>
            <span class="pagination-controls__summary">{{ formatDateTime(timeline.pageFrom, userSettingsStore.settings.dateTimeFormat) }} – {{ formatDateTime(timeline.pageTo, userSettingsStore.settings.dateTimeFormat) }}</span>
            <button class="button button--ghost" type="button" :disabled="timeline.nextCursor === null || loading" @click="showNextPage">Later</button>
          </div>
        </article>
      </div>
    </section>
  </AppShell>
</template>
